import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import minMax from "dayjs/plugin/minMax";
import isBetween from "dayjs/plugin/isBetween";
import { getActivityLocalDateKey, compareActivitiesByStart } from "./dateTime";

import { TOKENS } from "./designTokens";

dayjs.extend(isoWeek);
dayjs.extend(minMax);
dayjs.extend(isBetween);

export const METRICS = {
  distance: { key: "distance", label: "Distance", unit: "km", color: TOKENS.analytics.blue },
  time: { key: "time", label: "Time", unit: "h", color: TOKENS.analytics.purple },
  elevation: { key: "elevation", label: "Elev Gain", unit: "m", color: TOKENS.analytics.green },
};

export function getActivityDate(activity) {
  const key = getActivityLocalDateKey(activity);
  if (!key) return null;
  const d = dayjs(`${key}T12:00:00`);
  return d.isValid() ? d : null;
}

/** One row per canonical activity id. Same rule as the backend list dedupe. */
export function dedupeActivitiesById(activities) {
  const seen = new Set();
  const unique = [];
  for (const activity of activities || []) {
    const identity = String(activity?.id || activity?.activity_id || "");
    if (identity && seen.has(identity)) continue;
    if (identity) seen.add(identity);
    unique.push(activity);
  }
  return unique;
}

function nextMonthStartKey(monthKey) {
  const [yearText, monthText] = monthKey.split("-");
  const year = Number(yearText);
  const month = Number(monthText);
  const nextMonth = month === 12 ? 1 : month + 1;
  const nextYear = month === 12 ? year + 1 : year;
  return `${nextYear}-${String(nextMonth).padStart(2, "0")}-01`;
}

/**
 * Activities whose athlete-local date is in this calendar month.
 * Month start is inclusive. The next month start is exclusive.
 */
export function activitiesForLocalMonth(activities, monthKey) {
  if (!/^\d{4}-\d{2}$/.test(String(monthKey || ""))) return [];
  const start = `${monthKey}-01`;
  const next = nextMonthStartKey(monthKey);
  const matched = (activities || []).filter((activity) => {
    const key = getActivityLocalDateKey(activity);
    return Boolean(key) && key >= start && key < next;
  });
  return dedupeActivitiesById(matched);
}

export function formatWindowLabel(start, end) {
  return `${start.format("MMM D, YYYY")} – ${end.format("MMM D, YYYY")}`;
}

export function formatBucketTitle(bucket, granularity) {
  if (!bucket) return "All Activities";
  if (granularity === "monthly") return `Activities for ${bucket.rangeLabel}`;
  return `Activities for ${bucket.rangeLabel}`;
}
export function activityInWindow(activity, windowStart, windowEnd) {
  const d = getActivityDate(activity);
  return d && inRange(d, windowStart.startOf("day"), windowEnd.endOf("day"));
}

/** Rolling 12-month windows from earliest activity through today. */
export function buildYearWindows(activities) {
  const dates = activities.map(getActivityDate).filter(Boolean);
  if (!dates.length) return [];

  const earliest = dayjs.min(...dates).startOf("day");
  const latest = dayjs.max(...dates).endOf("day");
  const today = dayjs().endOf("day");
  let end = dayjs.max(latest, today);

  const windows = [];
  while (windows.length < 12) {
    let start = end.subtract(1, "year").add(1, "day").startOf("day");
    if (start.isBefore(earliest)) start = earliest;

    windows.unshift({
      id: `${start.format("YYYY-MM-DD")}_${end.format("YYYY-MM-DD")}`,
      start,
      end,
      label: formatWindowLabel(start, end),
    });

    if (start.isSame(earliest, "day") || start.isBefore(earliest)) break;
    end = start.subtract(1, "day").endOf("day");
  }

  return windows;
}

function aggregateActivities(list) {
  return list.reduce(
    (acc, a) => ({
      distance_m: acc.distance_m + (a.total_distance_m || 0),
      duration_s: acc.duration_s + (a.duration_seconds || 0),
      elevation_m: acc.elevation_m + (a.elevation_gain_m || 0),
      count: acc.count + 1,
    }),
    { distance_m: 0, duration_s: 0, elevation_m: 0, count: 0 }
  );
}

function inRange(date, start, end) {
  return date.isBetween(start, end, null, "[]");
}

function formatRangeLabel(start, end) {
  return `${start.format("MMM D, YYYY")} - ${end.format("MMM D, YYYY")}`;
}

export function formatIsoWeekKey(date) {
  return `${date.isoWeekYear()}-W${String(date.isoWeek()).padStart(2, "0")}`;
}

function createBucket({ key, label, rangeLabel, start, end, activities, granularity }) {
  const stats = aggregateActivities(activities);
  const now = dayjs();
  const isPartial =
    granularity === "monthly"
      ? end.endOf("month").isAfter(now, "day")
      : end.endOf("isoWeek").isAfter(now, "day");
  const isCurrent = now.isBetween(start, end, null, "[]");

  return {
    key,
    label,
    rangeLabel,
    start,
    end,
    ...stats,
    isPartial,
    isCurrent,
    activityIds: new Set(activities.map((a) => a.id)),
  };
}

export function buildMonthlyBuckets(windowStart, windowEnd, activities) {
  const buckets = [];
  let cursor = windowStart.startOf("month");

  while (cursor.isBefore(windowEnd) || cursor.isSame(windowEnd, "month")) {
    const monthStart = cursor.startOf("month");
    const monthEnd = cursor.endOf("month");
    const bucketStart = dayjs.max(monthStart, windowStart.startOf("day"));
    const bucketEnd = dayjs.min(monthEnd, windowEnd.endOf("day"));
    const monthKey = cursor.format("YYYY-MM");
    const inBucket = activitiesForLocalMonth(activities, monthKey);

    const showYear =
      cursor.month() === 0 ||
      cursor.isSame(windowStart, "month") ||
      cursor.isSame(windowEnd, "month");
    buckets.push(
      createBucket({
        key: monthKey,
        label: showYear ? cursor.format("MMM YYYY") : cursor.format("MMM"),
        rangeLabel: cursor.format("MMM YYYY"),
        start: bucketStart,
        end: bucketEnd,
        activities: inBucket,
        granularity: "monthly",
      })
    );
    cursor = cursor.add(1, "month");
  }

  return buckets;
}

export function buildWeeklyBuckets(windowStart, windowEnd, activities) {
  const buckets = [];
  let cursor = windowStart.startOf("isoWeek");

  while (cursor.isBefore(windowEnd) || cursor.isSame(windowEnd, "isoWeek")) {
    const weekStart = cursor.startOf("isoWeek");
    const weekEnd = cursor.endOf("isoWeek");
    const bucketStart = dayjs.max(weekStart, windowStart.startOf("day"));
    const bucketEnd = dayjs.min(weekEnd, windowEnd.endOf("day"));
    const inBucket = activities.filter((a) => {
      const d = getActivityDate(a);
      return d && inRange(d, bucketStart, bucketEnd);
    });

    const axisLabel =
      bucketStart.date() <= 7 || bucketStart.isSame(windowStart, "day")
        ? bucketStart.format("MMM")
        : "";

    buckets.push(
      createBucket({
        key: formatIsoWeekKey(weekStart),
        label: axisLabel,
        rangeLabel: formatRangeLabel(bucketStart, bucketEnd),
        start: bucketStart,
        end: bucketEnd,
        activities: inBucket,
        granularity: "weekly",
      })
    );
    cursor = weekStart.add(1, "week");
  }

  return buckets;
}

export function getMetricValue(bucket, metricKey, unitSystem = "metric") {
  if (!bucket) return 0;
  if (metricKey === "distance") {
    return unitSystem === "imperial"
      ? bucket.distance_m / 1609.34
      : bucket.distance_m / 1000;
  }
  if (metricKey === "time") return bucket.duration_s / 3600;
  if (metricKey === "elevation") return bucket.elevation_m;
  return 0;
}

export function formatMetricAxis(value, metricKey, unitSystem = "metric") {
  if (metricKey === "distance") {
    const unit = unitSystem === "imperial" ? "mi" : "km";
    return `${Math.round(value)} ${unit}`;
  }
  if (metricKey === "time") {
    if (value >= 1) return `${Math.round(value)}h`;
    return `${Math.round(value * 60)}m`;
  }
  if (metricKey === "elevation") return `${Math.round(value)}`;
  return String(Math.round(value));
}

export function findDefaultBucket(buckets) {
  if (!buckets.length) return null;
  const current = buckets.find((b) => b.isCurrent);
  if (current) return current;
  for (let i = buckets.length - 1; i >= 0; i -= 1) {
    if (buckets[i].count > 0) return buckets[i];
  }
  return buckets[buckets.length - 1];
}

export function activitiesInBucket(activities, bucket) {
  if (!bucket?.start || !bucket?.end) return [];
  if (/^\d{4}-\d{2}$/.test(String(bucket.key || ""))) {
    return activitiesForLocalMonth(activities, bucket.key);
  }
  const rangeStart = bucket.start.startOf("day");
  const rangeEnd = bucket.end.endOf("day");
  return dedupeActivitiesById(activities.filter((a) => {
    const d = getActivityDate(a);
    return d && inRange(d, rangeStart, rangeEnd);
  }));
}

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export function toDateKey(date) {
  if (!date) return null;
  const d = dayjs(date);
  return d.isValid() ? d.format("YYYY-MM-DD") : null;
}

/** Visible calendar grid range including leading/trailing outside-month cells. */
export function calendarGridFetchRange(year, month) {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const gridStart = new Date(first);
  gridStart.setDate(gridStart.getDate() - gridStart.getDay());
  const gridEnd = new Date(last);
  gridEnd.setDate(gridEnd.getDate() + (6 - gridEnd.getDay()));
  gridEnd.setHours(23, 59, 59, 999);
  return {
    startTime: gridStart.toISOString(),
    endTime: gridEnd.toISOString(),
  };
}

/** Unique calendar years from activities, newest first, capped at maxYears. */
export function buildCalendarYearOptions(activities, maxYears = 10) {
  const years = new Set();
  for (const a of activities) {
    const d = getActivityDate(a);
    if (d) years.add(d.year());
  }
  if (!years.size) {
    const current = dayjs().year();
    return [{ value: String(current), label: String(current) }];
  }
  return Array.from(years)
    .sort((a, b) => b - a)
    .slice(0, maxYears)
    .map((y) => ({ value: String(y), label: String(y) }));
}

export function buildCalendarMonthOptions(year) {
  const y = Number(year);
  return MONTH_NAMES.map((name, i) => ({
    value: String(i),
    label: name,
    year: y,
    month: i,
  }));
}

/** Map of YYYY-MM-DD → activities on that day. */
export function groupActivitiesByDay(activities) {
  const map = new Map();
  for (const a of activities) {
    const d = getActivityDate(a);
    if (!d) continue;
    const key = d.format("YYYY-MM-DD");
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(a);
  }
  for (const list of map.values()) {
    list.sort(compareActivitiesByStart);
  }
  return map;
}

export function getActivitiesForDate(activities, date, dayMap) {
  const key = toDateKey(date);
  if (!key) return [];
  const map = dayMap || groupActivitiesByDay(activities);
  return map.get(key) || [];
}

/** Date objects for days with at least one activity in a given year/month. */
export function getRideDatesInMonth(activities, year, month) {
  const map = groupActivitiesByDay(activities);
  const prefix = `${year}-${String(month + 1).padStart(2, "0")}-`;
  const dates = [];
  for (const key of map.keys()) {
    if (key.startsWith(prefix)) {
      dates.push(new Date(key + "T12:00:00"));
    }
  }
  return dates;
}

export function getActivitiesInMonth(activities, year, month) {
  const prefix = `${year}-${String(month + 1).padStart(2, "0")}-`;
  return activities.filter((a) => {
    const d = getActivityDate(a);
    return d && d.format("YYYY-MM-DD").startsWith(prefix);
  });
}

export function findDefaultCalendarYear(activities) {
  const options = buildCalendarYearOptions(activities);
  const current = String(dayjs().year());
  if (options.some((o) => o.value === current)) return current;
  return options[0]?.value ?? current;
}

export function findDefaultCalendarMonth(activities, year) {
  const y = Number(year);
  const now = dayjs();
  if (y === now.year()) return String(now.month());

  const monthActivities = new Map();
  for (const a of activities) {
    const d = getActivityDate(a);
    if (!d || d.year() !== y) continue;
    const m = d.month();
    if (!monthActivities.has(m)) monthActivities.set(m, 0);
    monthActivities.set(m, monthActivities.get(m) + 1);
  }
  if (!monthActivities.size) return "0";

  let best = 0;
  let bestCount = 0;
  for (const [m, count] of monthActivities) {
    if (count > bestCount) {
      best = m;
      bestCount = count;
    }
  }
  return String(best);
}
